<template>
  <div class="admin-blog">
    <div class="section-header">
      <h2>📝 Blog Yönetimi</h2>
      <p>Blog yazılarını ekle, düzenle ve yayınla. Taslak olarak kaydedip sonra yayınlayabilirsin.</p>
    </div>

    <!-- Üst Bar -->
    <div class="admin-blog-toolbar">
      <button @click="openNewBlogModal" class="btn-primary">+ Yeni Blog Yazısı</button>
      <div class="stats">
        Toplam: <strong>{{ blogs.length }}</strong> |
        Yayında: <strong>{{ publishedCount }}</strong> |
        Taslak: <strong>{{ draftCount }}</strong>
      </div>
    </div>

    <!-- Yükleniyor -->
    <div v-if="loading" class="loading">Bloglar yükleniyor...</div>

    <!-- Boş durum -->
    <div v-else-if="blogs.length === 0" class="empty-state">
      <p>Henüz blog yazısı yok. "+ Yeni Blog Yazısı" ile başla.</p>
    </div>

    <!-- Blog Listesi -->
    <div v-else class="blog-list">
      <div v-for="blog in blogs" :key="blog.id" class="blog-card">
        <div class="blog-card-image" v-if="blog.image_url">
          <img :src="resolveImageUrl(blog.image_url)" :alt="blog.title" />
        </div>
        <div class="blog-card-body">
          <div class="blog-card-meta">
            <span :class="['status-badge', 'status-' + blog.status]">
              {{ blog.status === 'published' ? '✅ Yayında' : '📝 Taslak' }}
            </span>
            <span v-if="blog.category" class="category-badge">{{ blog.category }}</span>
            <span v-if="blog.featured" class="featured-badge">⭐ Öne Çıkan</span>
          </div>
          <h3>{{ blog.title }}</h3>
          <p class="blog-card-preview">{{ blog.content.substring(0, 200) }}...</p>
          <div class="blog-card-footer">
            <span class="date">{{ formatDate(blog.created_at) }}</span>
            <div class="actions">
              <button @click="editBlog(blog)" class="btn-edit" title="Düzenle">✏️</button>
              <button @click="deleteBlog(blog)" class="btn-delete" title="Sil">🗑️</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Yeni / Düzenle Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingBlog ? '✏️ Blog Düzenle' : '+ Yeni Blog Yazısı' }}</h3>
          <button @click="closeModal" class="modal-close">&times;</button>
        </div>

        <form @submit.prevent="submitBlog" class="modal-body">
          <div class="form-group">
            <label>Başlık *</label>
            <input v-model="form.title" type="text" required maxlength="500" placeholder="Blog yazısının başlığı" />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Kategori</label>
              <input v-model="form.category" type="text" maxlength="100" placeholder="Örn: TYT-AYT Hazırlık" />
            </div>
            <div class="form-group">
              <label>Durum</label>
              <select v-model="form.status">
                <option value="draft">📝 Taslak</option>
                <option value="published">✅ Yayında</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>
              <input v-model="form.featured" type="checkbox" />
              ⭐ Öne çıkanlar arasında göster
            </label>
          </div>

          <!-- Görsel -->
          <div class="form-group">
            <label>Kapak Görseli</label>
            <div class="image-tabs">
              <button type="button"
                      :class="['tab-btn', { active: imageMode === 'url' }]"
                      @click="imageMode = 'url'">🔗 URL Yapıştır</button>
              <button type="button"
                      :class="['tab-btn', { active: imageMode === 'upload' }]"
                      @click="imageMode = 'upload'">📤 Dosya Yükle</button>
            </div>
            <div v-if="imageMode === 'url'">
              <input v-model="form.image_url" type="text" placeholder="https://... veya /uploads/blog/xxx.jpg" />
            </div>
            <div v-else>
              <input type="file" accept="image/jpeg,image/png,image/webp" @change="handleFileUpload" :disabled="uploading" />
              <p v-if="uploading" class="upload-status">Yükleniyor...</p>
              <p v-if="uploadError" class="upload-error">{{ uploadError }}</p>
              <p v-if="form.image_url && !uploading" class="upload-status">✅ Yüklendi: {{ form.image_url }}</p>
            </div>
            <div v-if="form.image_url" class="image-preview">
              <img :src="resolveImageUrl(form.image_url)" alt="Önizleme" />
            </div>
          </div>

          <div class="form-group">
            <label>İçerik *</label>
            <textarea v-model="form.content" required rows="15"
                      placeholder="Blog içeriğini buraya yaz. Paragraflar arasında boş satır bırakabilirsin."></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Meta Description (SEO)</label>
              <input v-model="form.meta_description" type="text" maxlength="160" placeholder="Google aramada görünecek özet (max 160 karakter)" />
            </div>
            <div class="form-group">
              <label>Meta Keywords (SEO)</label>
              <input v-model="form.meta_keywords" type="text" placeholder="virgülle, ayırılmış, kelimeler" />
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModal" class="btn-secondary">İptal</button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? 'Kaydediliyor...' : (editingBlog ? 'Güncelle' : 'Oluştur') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Silme Onayı -->
    <div v-if="deleteConfirm" class="modal-overlay" @click.self="deleteConfirm = null">
      <div class="modal-content confirm-modal">
        <h3>Silmek istediğinden emin misin?</h3>
        <p><strong>{{ deleteConfirm.title }}</strong> yazısı kalıcı olarak silinecek.</p>
        <div class="form-actions">
          <button @click="deleteConfirm = null" class="btn-secondary">İptal</button>
          <button @click="confirmDelete" class="btn-danger">Evet, Sil</button>
        </div>
      </div>
    </div>

    <AppErrorModal :message="errorPopup" @close="errorPopup = ''" />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import AppErrorModal from '@/components/AppErrorModal.vue'

const emptyForm = () => ({
  title: '',
  content: '',
  category: '',
  image_url: '',
  meta_description: '',
  meta_keywords: '',
  status: 'draft',
  featured: false
})

export default {
  name: 'AdminBlog',
  components: { AppErrorModal },
  setup() {
    const blogs = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const uploading = ref(false)
    const errorPopup = ref('')
    const uploadError = ref('')

    const showModal = ref(false)
    const editingBlog = ref(null)
    const deleteConfirm = ref(null)
    const imageMode = ref('url')

    const form = ref(emptyForm())

    const publishedCount = computed(() => blogs.value.filter(b => b.status === 'published').length)
    const draftCount = computed(() => blogs.value.filter(b => b.status === 'draft').length)

    const authHeader = () => ({
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })

    const loadBlogs = async () => {
      loading.value = true
      try {
        const res = await axios.get('/api/admin/blogs', authHeader())
        blogs.value = res.data || []
      } catch (err) {
        console.error('Bloglar yüklenemedi:', err)
      } finally {
        loading.value = false
      }
    }

    const openNewBlogModal = () => {
      editingBlog.value = null
      form.value = emptyForm()
      imageMode.value = 'url'
      uploadError.value = ''
      showModal.value = true
    }

    const editBlog = (blog) => {
      editingBlog.value = blog
      form.value = { ...blog }
      imageMode.value = blog.image_url ? 'url' : 'url'
      uploadError.value = ''
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      editingBlog.value = null
      form.value = emptyForm()
    }

    const submitBlog = async () => {
      saving.value = true
      try {
        if (editingBlog.value) {
          const res = await axios.put(`/api/admin/blogs/${editingBlog.value.id}`, form.value, authHeader())
          const idx = blogs.value.findIndex(b => b.id === editingBlog.value.id)
          if (idx !== -1 && res.data.id) blogs.value.splice(idx, 1, res.data)
        } else {
          const res = await axios.post('/api/admin/blogs', form.value, authHeader())
          if (res.data.id) blogs.value.unshift(res.data)
        }
        closeModal()
      } catch (err) {
        errorPopup.value = 'Kaydetme hatası: ' + (err.response?.data?.error || err.message)
      } finally {
        saving.value = false
      }
    }

    const handleFileUpload = async (e) => {
      const file = e.target.files?.[0]
      if (!file) return
      uploadError.value = ''
      uploading.value = true
      try {
        const formData = new FormData()
        formData.append('image', file)
        const res = await axios.post('/api/admin/blogs/upload-image', formData, {
          headers: {
            ...authHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        })
        form.value.image_url = res.data.url
      } catch (err) {
        uploadError.value = err.response?.data?.error || 'Yükleme başarısız'
      } finally {
        uploading.value = false
        e.target.value = ''
      }
    }

    const deleteBlog = (blog) => {
      deleteConfirm.value = blog
    }

    const confirmDelete = async () => {
      if (!deleteConfirm.value) return
      try {
        await axios.delete(`/api/admin/blogs/${deleteConfirm.value.id}`, authHeader())
        blogs.value = blogs.value.filter(b => b.id !== deleteConfirm.value.id)
        deleteConfirm.value = null
      } catch (err) {
        errorPopup.value = 'Silme hatası: ' + (err.response?.data?.error || err.message)
      }
    }

    // Görsel URL çözümleme: eğer /uploads/... ile başlıyorsa backend URL prefix'i ekle
    const resolveImageUrl = (url) => {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) return url
      if (url.startsWith('/uploads/')) return axios.defaults.baseURL + url
      return url
    }

    const formatDate = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }

    onMounted(loadBlogs)

    return {
      blogs, loading, saving, uploading, uploadError, errorPopup,
      showModal, editingBlog, deleteConfirm, imageMode, form,
      publishedCount, draftCount,
      openNewBlogModal, editBlog, closeModal, submitBlog,
      handleFileUpload, deleteBlog, confirmDelete,
      resolveImageUrl, formatDate
    }
  }
}
</script>

<style scoped>
.admin-blog {
  padding: 1rem 0;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h2 {
  color: #fff;
  margin: 0 0 0.5rem 0;
}

.section-header p {
  color: #cbd5e1;
  margin: 0;
}

.admin-blog-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  color: #cbd5e1;
}

.admin-blog-toolbar .stats {
  font-size: 0.95rem;
}

.admin-blog-toolbar .stats strong {
  color: #fff;
}

.loading, .empty-state {
  text-align: center;
  padding: 3rem;
  color: #cbd5e1;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
}

/* Blog kartları */
.blog-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
}

.blog-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease;
}

.blog-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.blog-card-image {
  height: 180px;
  overflow: hidden;
}

.blog-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-card-body {
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.blog-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.status-badge {
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.status-published {
  background: #dcfce7;
  color: #166534;
}

.status-badge.status-draft {
  background: #fef3c7;
  color: #92400e;
}

.category-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
}

.featured-badge {
  background: #fef9c3;
  color: #713f12;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
}

.blog-card h3 {
  margin: 0.25rem 0;
  font-size: 1.1rem;
  color: #1a1a1a;
  line-height: 1.35;
}

.blog-card-preview {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
  flex: 1;
}

.blog-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.blog-card-footer .date {
  color: #64748b;
  font-size: 0.85rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-edit, .btn-delete {
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.15s ease;
}

.btn-edit:hover { background: #eff6ff; border-color: #3b82f6; }
.btn-delete:hover { background: #fef2f2; border-color: #ef4444; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  padding: 1rem;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
}

.modal-content.confirm-modal {
  max-width: 480px;
  padding: 1.5rem 2rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.modal-header h3 {
  margin: 0;
  color: #1a1a1a;
  font-size: 1.25rem;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 1.75rem;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.modal-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 600px) {
  .form-row { grid-template-columns: 1fr; }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-group label {
  font-weight: 500;
  color: #334155;
  font-size: 0.9rem;
}

.form-group input[type="text"],
.form-group select,
.form-group textarea {
  padding: 0.6rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: inherit;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.form-group textarea {
  resize: vertical;
  min-height: 200px;
  line-height: 1.5;
}

/* Görsel tabs */
.image-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.tab-btn {
  padding: 0.4rem 0.9rem;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}

.tab-btn.active {
  background: #3b82f6;
  color: #fff;
  border-color: #3b82f6;
}

.upload-status { color: #059669; font-size: 0.85rem; margin: 0.5rem 0 0 0; }
.upload-error { color: #dc2626; font-size: 0.85rem; margin: 0.5rem 0 0 0; }

.image-preview {
  margin-top: 0.75rem;
  border-radius: 6px;
  overflow: hidden;
  max-width: 300px;
}

.image-preview img {
  width: 100%;
  display: block;
}

/* Butonlar */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.btn-primary {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: #fff;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.btn-primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
  padding: 0.6rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
}

.btn-secondary:hover { background: #e2e8f0; }

.btn-danger {
  background: #dc2626;
  color: #fff;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-danger:hover { background: #b91c1c; }
</style>
